// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import get from 'lodash/get'
import moment from 'moment'
import React, { Component } from 'react'
import { Mutation, Query } from 'react-apollo'
import Loader from '../../common/loader/Loader'
import { i18n } from '../index'
import { fetchEfficiencyFactors } from '../station/stationQueries'
import css from './BudgetPlanning.css'
import { fetchBudgetAndForecastAndPlan, fetchBudgetAndPlan, upsertMonthlySalesPlanNote } from './budgetPlanningQueries'
import GraphSection from './GraphSection'
import IndexSection from './IndexSection'
import NoteDialog from './NoteDialog'
import ProfitCenterSelect from './ProfitCenterSelect'
import StatsSection from './StatsSection'
import type { PlanNote, ProfitCenter } from './types'
import { calculateIndices } from './utils'
type Props = {
  options: Array<ProfitCenter>
  stationId: string
}
type State = {
  selectedOption: ProfitCenter
  currentMonth: number
  editable: boolean
  noteDialogIsOpen: boolean
  selectedNote: PlanNote | null
}
export default class BudgetPlanningPage extends Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      selectedOption: props.options[0],
      currentMonth: Number(moment().format('M')),
      editable: false,
      noteDialogIsOpen: false,
      selectedNote: null,
    }
  }

  scrollToPosition = (position) => {
    setTimeout(() => {
      window.scrollTo({
        left: 0,
        top: position,
        behavior: 'smooth',
      })
    }, 500)
  }
  getDefaultDailyData = (currentMonth: number, dataType: string) => {
    const dailyData = []
    const numberOfDays: number = moment()
      .startOf('year')
      .add(currentMonth - 1, 'months')
      .daysInMonth()
    const startOfMonth = moment()
      .startOf('year')
      .add(currentMonth - 1, 'months')
      .startOf('month')

    for (let i = 0; i < numberOfDays; i++) {
      const date = startOfMonth.clone().add(i, 'days').format('YYYY-MM-DD')
      let data

      switch (dataType) {
        case 'budget':
          data = {
            date,
            budgetedSales: 0,
            budgetedHours: 0,
            actualHours: 0,
            actualSales: 0,
            budgetedEfficiency: 0,
          }
          break

        default:
          data = {
            date,
            sales: 0,
          }
          break
      }

      dailyData.push(data)
    }

    return dailyData
  }
  onOptionChange = (name: string) => {
    const selectedOption = this.props.options.find((option) => option.name === name)
    this.setState({
      selectedOption,
      editable: false,
    })
  }
  onMonthChange = (step: number) => {
    const { currentMonth } = this.state
    this.setState({
      currentMonth: currentMonth + step,
      editable: false,
    })
    this.scrollToPosition(window.scrollY)
  }
  toggleEditMode = () => {
    this.setState({
      editable: !this.state.editable,
    })
  }
  getQueryVariables = (thisMonth: string, lastMonth: string) => {
    const { stationId } = this.props
    const { selectedOption } = this.state
    const queryVariables: any = {
      stationId,
      thisMonth,
      lastMonth,
    }

    if (selectedOption.type !== 'Station') {
      queryVariables.profitCenterId = +selectedOption.id
    }

    return queryVariables
  }
  onNoteDialogOpen = (note: PlanNote) => {
    this.setState({
      noteDialogIsOpen: true,
      selectedNote: note,
    })
  }
  onNoteDialogClose = () => {
    this.setState({
      selectedNote: null,
      noteDialogIsOpen: false,
    })
  }
  onNoteSave = (onMutate: (...args: Array<any>) => any, noteInput: PlanNote, refetch: (...args: Array<any>) => any) => {
    onMutate({
      variables: {
        noteInput,
      },
    })
    refetch()
  }

  render() {
    const { options, stationId } = this.props
    const { selectedOption, currentMonth, editable, noteDialogIsOpen, selectedNote } = this.state
    const isStationPage = 'Station' === selectedOption.type
    const currentDayOfMonth = new Date().getDate()
    const thisMonth = moment()
      .startOf('year')
      .add(currentMonth - 1, 'months')
      .format('YYYY-MM-DD')
    const lastMonthDate = moment()
      .startOf('year')
      .add(currentMonth - 2, 'months')
    const lastMonth = lastMonthDate.clone().format('YYYY-MM-DD')
    const monthAgoToday = lastMonthDate.clone().add(currentDayOfMonth, 'days').format('YYYY-MM-DD')
    const monthWeActuallyLiveIn = moment().startOf('month').format('YYYY-MM-DD')
    const QUERY = isStationPage ? fetchBudgetAndForecastAndPlan : fetchBudgetAndPlan
    const QUERY_VARS = this.getQueryVariables(thisMonth, lastMonth)
    return (
      <Query query={QUERY} variables={QUERY_VARS} fetchPolicy='network-only'>
        {({ loading, error, data, refetch }) => {
          // Budget data
          const thisMonthBudgetData = get(data, ['budgetThisMonth', '0'], {})
          let { dailyData: dailyBudgetData = [] } = thisMonthBudgetData
          const { date } = thisMonthBudgetData

          if (!dailyBudgetData.length) {
            dailyBudgetData = this.getDefaultDailyData(currentMonth, 'budget')
          }

          const lastMonthBudgetData = get(data, ['budgetLastMonth', '0'], {})
          let { dailyData: lastMonthDailyBudgetData = [] } = lastMonthBudgetData

          if (!lastMonthDailyBudgetData.length) {
            lastMonthDailyBudgetData = this.getDefaultDailyData(currentMonth - 1, 'budget')
          }

          // Forecast Data
          const thisMonthForecastData = get(data, 'forecastThisMonth', {})
          let { dailyData: dailyForecastData = [] } = thisMonthForecastData

          if (!dailyForecastData.length) {
            dailyForecastData = this.getDefaultDailyData(currentMonth, 'forecast')
          }

          // Plan Data
          const thisMonthPlanData = get(data, 'planThisMonth', {})
          const { dailyData: dailyPlanData = [] } = thisMonthPlanData
          // Graphs data
          const lastMonthPlanData = get(data, ['planLastMonth', 'dailyData'], [])
          const { actuals, ...lastMonthCumulativePlans } = lastMonthPlanData
            .filter(({ date }) => date <= monthAgoToday)
            .reduce(
              (acc, plan) => ({
                plannedSales: acc.plannedSales + plan.plannedSales,
                plannedHours: acc.plannedHours + plan.plannedHours,
                plannedEfficiency: acc.plannedEfficiency + plan.plannedEfficiency,
                actuals: {
                  actualHours: acc.actuals.actualHours + plan.actualHours,
                  actualSales: acc.actuals.actualSales + plan.actualSales,
                  actualEfficiency: acc.actuals.actualEfficiency + plan.actualEfficiency,
                },
              }),
              {
                plannedSales: 0,
                plannedHours: 0,
                plannedEfficiency: 0,
                actuals: {
                  actualHours: 0,
                  actualSales: 0,
                  actualEfficiency: 0,
                },
              },
            )
          const lastMonthCumulativeBudgets = lastMonthDailyBudgetData
            .filter(({ date }) => date <= monthAgoToday)
            .reduce(
              (acc, budget) => ({
                plannedSales: acc.plannedSales + budget.budgetedSales,
                plannedHours: acc.plannedHours + budget.budgetedHours,
                plannedEfficiency: acc.plannedEfficiency + budget.budgetedEfficiency,
              }),
              {
                plannedSales: 0,
                plannedHours: 0,
                plannedEfficiency: 0,
              },
            )
          const budgetIndices = calculateIndices(lastMonthCumulativeBudgets, actuals)
          const plannedIndices = calculateIndices(lastMonthCumulativePlans, actuals)
          const graphData = dailyPlanData.map((x) => {
            const budget = dailyBudgetData.filter((y) => y.date === x.date).pop()
            return {
              ...x,
              budgetedEfficiency: budget ? budget.budgetedEfficiency : 0,
              budgetedHours: budget ? budget.budgetedHours : 0,
              budgetedSales: budget ? budget.budgetedSales : 0,
            }
          })
          return (
            <Loader
              {...{
                loading,
                error,
              }}
            >
              <Mutation mutation={upsertMonthlySalesPlanNote}>
                {(onMutate) => (
                  <NoteDialog
                    isOpen={noteDialogIsOpen}
                    onClose={this.onNoteDialogClose}
                    dailyNote={selectedNote}
                    stationId={stationId}
                    onSave={(noteInput) => this.onNoteSave(onMutate, noteInput, refetch)}
                  />
                )}
              </Mutation>

              <div className={css.fieldLabel}>{i18n.t('budgetPlanning.profitCenter')}</div>
              <ProfitCenterSelect options={options} selectedOption={selectedOption} onChange={this.onOptionChange} />
              <div className={css.rowContainer}>
                <IndexSection indexes={plannedIndices} label={i18n.t('budgetPlanning.planIndices')} />
                <IndexSection indexes={budgetIndices} label={i18n.t('budgetPlanning.budgetIndices')} />
              </div>
              <Query
                query={fetchEfficiencyFactors}
                variables={{
                  stationId,
                }}
              >
                {({ data, error, loading }) => (
                  <Loader
                    {...{
                      error,
                      loading,
                    }}
                  >
                    <StatsSection
                      currentMonth={currentMonth}
                      onMonthChange={this.onMonthChange}
                      dailyBudgetData={dailyBudgetData}
                      dailyForecastData={dailyForecastData}
                      dailyPlanData={dailyPlanData}
                      editable={editable}
                      startDate={date}
                      stationId={stationId}
                      toggleEditMode={this.toggleEditMode}
                      selectedOption={selectedOption}
                      efficiencyFactors={get(data, 'efficiencyFactors', [])}
                      onNoteDialogOpen={this.onNoteDialogOpen}
                    />
                  </Loader>
                )}
              </Query>
              <GraphSection
                month={thisMonth}
                data={graphData}
                displayCumulativeValues={monthWeActuallyLiveIn === thisMonth}
              />
            </Loader>
          )
        }}
      </Query>
    )
  }
}
